import { useMemo } from 'react'
import qrcode from 'qrcode-generator'
import './qr.css'

type Props = {
  /** What the code encodes. For MFA this is the `otpauth://` URI. */
  value: string
  /**
   * The accessible name. A QR is an image of a string, and a screen-reader user
   * cannot scan it — so this says what it is FOR, and the typeable secret
   * beside it is the real alternative. Never the URI: it contains the secret.
   */
  label: string
  /** Rendered size in CSS pixels. The SVG scales; the module grid does not. */
  size?: number
}

/**
 * Four modules of blank on every side, REQUIRED by the spec.
 *
 * Scanners use the margin to find the code's edges, and without it many simply
 * never acquire — the single most common reason a hand-placed QR fails.
 */
const QUIET = 4

/**
 * A QR code as SVG, from `qrcode-generator` (MIT, zero dependencies).
 *
 * **Why a dependency at all.** CLAUDE.md says charts are hand-rolled on
 * d3-scale/d3-shape and forbids a chart library. A QR is not a chart — it is an
 * error-correcting binary encoding, ~300 lines of Reed-Solomon where a subtle
 * bug produces a code that scans as the wrong string rather than failing
 * visibly. That is the opposite of the property the hand-rolling rule protects,
 * so this was taken as an explicit decision rather than assumed either way. The
 * rule's substance is kept: the library returns a boolean matrix, and this
 * component owns the DOM and takes its colours from tokens.
 *
 * `QrCode.test.tsx` compares the rendered modules against an independent
 * encode, which is the check the dependency was accepted in order to have.
 *
 * **Error correction level M** — 15% recoverable. The URI is short, so a higher
 * level costs nothing in size, but M is what authenticator apps have been
 * scanning for a decade and there is no reason to be unusual here.
 */
export function QrCode({ value, label, size = 200 }: Props) {
  // Memoised on the value: encoding runs Reed-Solomon over the whole string,
  // and this renders directly above a text field — without it every keystroke
  // in the code box re-encodes the QR.
  const { count, d } = useMemo(() => {
    // 0 = pick the smallest version that fits. `make()` must run before a read.
    const qr = qrcode(0, 'M')
    qr.addData(value)
    qr.make()
    const modules = qr.getModuleCount()

    // One path for every dark module beats one <rect> each: a version-4 code is
    // ~1000 modules, and a thousand elements is a real cost on a phone.
    let path = ''
    for (let row = 0; row < modules; row += 1) {
      for (let col = 0; col < modules; col += 1) {
        if (qr.isDark(row, col)) path += `M${col + QUIET} ${row + QUIET}h1v1h-1z`
      }
    }
    return { count: modules, d: path }
  }, [value])

  const span = count + QUIET * 2

  return (
    <svg
      className="qr"
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox={`0 0 ${span} ${span}`}
      // `shapeRendering` matters here: the default antialiases module edges into
      // grey, which is what makes a small QR unreliable to scan.
      shapeRendering="crispEdges"
    >
      <rect className="qr__paper" width={span} height={span} />
      <path className="qr__ink" d={d} />
    </svg>
  )
}
